// biome-ignore lint/style/noRestrictedImports: window focus and document visibility listeners.
import { useEffect, useState } from "react";

import { fetchGitSha } from "@/api/health";

const POLL_MS = 5 * 60 * 1000;

function bundledGitSha(): string {
  return import.meta.env.VITE_GIT_SHA ?? "";
}

function shouldWatchForMismatch(): boolean {
  return Boolean(bundledGitSha()) && !import.meta.env.DEV;
}

async function serverShaDiffers(): Promise<boolean> {
  const serverSha = await fetchGitSha();
  const buildSha = bundledGitSha();
  return Boolean(serverSha && serverSha !== buildSha);
}

/** True when the running bundle SHA no longer matches `/api/health`. */
export function useAppVersionMismatch(): boolean {
  const [mismatch, setMismatch] = useState(false);

  useEffect(() => {
    if (!shouldWatchForMismatch()) {
      return;
    }

    let cancelled = false;
    let found = false;

    const check = async () => {
      if (cancelled || found) {
        return;
      }
      if (await serverShaDiffers()) {
        found = true;
        setMismatch(true);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        check();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", check);
    const interval = window.setInterval(check, POLL_MS);
    check();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", check);
      window.clearInterval(interval);
    };
  }, []);

  return mismatch;
}
